import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Textarea } from '../../ui/textarea';
import { Stamp, AlertTriangle } from 'lucide-react';

/**
 * Diálogo para forzar (o quitar) el V°B° de una factura que NO está conciliada.
 * El motivo es obligatorio (mínimo 5 caracteres) al forzar — por auditoría.
 * El componente se remonta por factura (key) → el motivo arranca vacío.
 */
export const VBForzadoDialog = ({ open, factura, onClose, onConfirm }) => {
  const [motivo, setMotivo] = useState('');
  const [saving, setSaving] = useState(false);
  const yaForzado = Boolean(factura?.vb_forzado);

  if (!factura) return null;

  const handleForzar = async () => {
    if (motivo.trim().length < 5) return;
    setSaving(true);
    try {
      await onConfirm({ forzar: true, motivo: motivo.trim() });
    } finally {
      setSaving(false);
    }
  };

  const handleQuitar = async () => {
    setSaving(true);
    try {
      await onConfirm({ forzar: false });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent data-testid="vb-forzado-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Stamp className="w-4 h-4 text-amber-600" /> V°B° forzado
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 text-sm">
          <div className="rounded-md bg-muted/50 p-2 text-xs">
            <div><b>Proveedor:</b> {factura.nombre_proveedor}</div>
            <div><b>Folio:</b> {factura.folio} · <b>Monto:</b> ${(factura.monto || 0).toLocaleString('es-CL')}</div>
          </div>

          <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-800">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>
              Esta factura <b>no está conciliada</b> (no llegó el estado de cuenta del
              proveedor o el folio no aparece). Al forzar el V°B° la autorizas para
              pago igual; quedará marcada en <b>color ámbar</b> y registrada con tu usuario.
            </span>
          </div>

          {!yaForzado && (
            <div>
              <label className="text-xs font-medium">Motivo (obligatorio)</label>
              <Textarea
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                placeholder="Ej: Proveedor sin estado de cuenta, factura validada con OC y recepción."
                rows={3}
                data-testid="vb-forzado-motivo"
              />
              <p className="text-[10px] text-muted-foreground mt-1">Mínimo 5 caracteres.</p>
            </div>
          )}

          {yaForzado && (
            <div className="rounded-md bg-amber-100 border border-amber-300 p-2 text-xs">
              Ya tiene V°B° forzado{factura.vb_forzado_por ? ` por ${factura.vb_forzado_por}` : ''}.
              {factura.vb_forzado_motivo ? <div className="mt-1"><b>Motivo:</b> {factura.vb_forzado_motivo}</div> : null}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="vb-forzado-cancel">
            Cancelar
          </Button>
          {yaForzado ? (
            <Button variant="destructive" onClick={handleQuitar} disabled={saving} data-testid="vb-forzado-quitar">
              Quitar V°B° forzado
            </Button>
          ) : (
            <Button
              className="bg-amber-600 hover:bg-amber-700 text-white"
              onClick={handleForzar}
              disabled={saving || motivo.trim().length < 5}
              data-testid="vb-forzado-confirm"
            >
              {saving ? 'Guardando…' : 'Forzar V°B°'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
